import React, { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, User, Shield, Briefcase, Headphones, Pencil } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import EditTeamMemberModal from "../../components/EditTeamMemberModal"; // Import Modal

// Recursive Tree Item
const TeamNodeItem = ({ node, onEdit }: { node: any, onEdit: (u: any) => void }) => {
  const [isOpen, setIsOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  // Icon Helper
  const getIcon = (role: string) => {
    switch(role) {
      case "ADMIN": return <Shield size={16} className="text-red-500"/>;
      case "MANAGER": return <Briefcase size={16} className="text-amber-500"/>;
      case "TEAM_LEADER": return <User size={16} className="text-blue-500"/>;
      case "AGENT": return <Headphones size={16} className="text-green-500"/>;
      default: return <User size={16}/>;
    }
  };

  return (
    <div className="ml-6 border-l border-zinc-700 pl-4 relative font-[Serif]">
      <div className="absolute top-4 -left-4 w-4 h-[1px] bg-zinc-700"></div>

      <div className="mb-4">
        <div className={`flex items-center gap-4 bg-[#1c1c1c] border border-zinc-800 p-4 rounded-lg transition-all group hover:border-zinc-600`}>
          
          {/* Expand/Collapse */}
          <div onClick={() => hasChildren && setIsOpen(!isOpen)} className="cursor-pointer">
             {hasChildren ? (isOpen ? <ChevronDown size={16} className="text-zinc-500"/> : <ChevronRight size={16} className="text-zinc-500"/>) : <span className="w-4 h-4"></span>}
          </div>

          <div className="bg-zinc-900 p-2 rounded-full border border-zinc-800">{getIcon(node.role)}</div>

          <div className="flex-1">
             <h4 className="text-white font-bold text-sm">{node.name}</h4>
             <p className="text-zinc-500 text-xs">{node.email}</p>
          </div>

          <span className="text-[10px] font-bold uppercase tracking-widest bg-zinc-800 px-2 py-1 rounded text-zinc-300 mr-4">
            {node.role.replace("_", " ")}
          </span>

          {/* EDIT BUTTON (Only visible on hover) */}
          <button 
            onClick={() => onEdit(node)}
            className="opacity-0 group-hover:opacity-100 bg-zinc-800 hover:bg-amber-600 hover:text-black text-white p-2 rounded transition-all"
            title="Edit Member"
          >
            <Pencil size={14} />
          </button>
        </div>

        {/* RECURSION */}
        {hasChildren && isOpen && (
          <div className="mt-2 animate-in slide-in-from-top-2 fade-in">
            {node.children.map((child: any) => (
              <TeamNodeItem key={child.id} node={child} onEdit={onEdit} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Subordinates = () => {
  const { token } = useAdminAuth();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit State
  const [editingUser, setEditingUser] = useState<any>(null);

  const fetchTeam = () => {
    setLoading(true);
    fetch("https://luxuriyahomes.com/api/admin/team/my-subordinates", {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => { setTree(data); setLoading(false); })
    .catch(err => console.error(err));
  };

  useEffect(() => { fetchTeam(); }, []);

  return (
    <div className="text-white min-h-screen">
      <div className="mb-8">
        <h2 className="text-3xl font-light">Hierarchy View</h2>
        <p className="text-zinc-500 text-sm">Manage & Edit your team structure</p>
      </div>

      {loading ? <p>Loading...</p> : (
        <div className="p-4">
          {tree.map((node: any) => (
             <TeamNodeItem key={node.id} node={node} onEdit={(u) => setEditingUser(u)} />
          ))}
        </div>
      )}

      {/* EDIT MODAL */}
      {editingUser && (
        <EditTeamMemberModal 
            user={editingUser} 
            onClose={() => setEditingUser(null)} 
            onSuccess={() => {
                setEditingUser(null);
                fetchTeam(); // Refresh Tree after Edit
            }}
        />
      )}
    </div>
  );
};

export default Subordinates;